import React, { useMemo } from "react";
import { Button } from "primereact/button";
import { DailyMapFilter } from "../../utils/daily-map";

export type Driver = {
  driverID: number;
  driverName: string;
  assigned: number;
  delivered: number;
  pending: number;
  cancelled: number;
  totaldelayed: number;
  color: string;
  unknown?: number;
};

type Props = {
  driversSummary: Driver[];
  historical?: boolean;
  selectedDriverId?: number | null;
  handleDriverOrdersClick: (
    driverID: number,
    status: DailyMapFilter
  ) => void;
};

export default function DriverSummaryPanel({
  driversSummary,
  handleDriverOrdersClick,
  historical = false,
  selectedDriverId,
}: Props) {
  const ICON_SIZE = 50;

  const pinSvgUrl = (color: string, text: number | string) => {
    const fontSize = Math.floor(ICON_SIZE / 7);
    const svg = `
      <svg width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2c-3.9 0-7 3.1-7 7 0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7z"
          fill="${color}" stroke="white" stroke-width="1"/>
        <text x="12" y="13" text-anchor="middle" font-size="${fontSize}" fill="black" font-weight="bold" font-family="sans-serif">${text}</text>
      </svg>`;
    return "data:image/svg+xml;base64," + btoa(svg);
  };

  const makeBtn = (
    driverID: number,
    count: number,
    type: "assigned" | "delivered" | "pending" | "cancelled" | "delayed",
    tooltip: string
  ) => {
    const colors: Record<typeof type, string> = {
      assigned: "#6b7280",
      delivered: "#22c55e",
      pending: "#3b82f6",
      cancelled: "#ef4444",
      delayed: "#f59e0b",
    };
    return (
      <Button
        key={type}
        tooltip={tooltip}
        tooltipOptions={{ position: "top" }}
        onClick={() => handleDriverOrdersClick(driverID, type)}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          minWidth: ICON_SIZE,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img
          src={pinSvgUrl(colors[type], count)}
          alt={tooltip}
          style={{ height: ICON_SIZE, width: ICON_SIZE }}
        />
      </Button>
    );
  };

  // Calculate max driver name width dynamically
  const maxNameWidth = useMemo(() => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return 100;
    context.font = "bold 14px sans-serif";
    let maxWidth = 0;
    driversSummary.forEach((d) => {
      const width = context.measureText(d.driverName).width;
      if (width > maxWidth) maxWidth = width;
    });
    return Math.ceil(maxWidth) + 20;
  }, [driversSummary]);

  const maxCountWidth = useMemo(() => {
    let max = 0;
    driversSummary.forEach((d) => {
      const largest = Math.max(d.assigned, d.delivered, d.pending, d.cancelled, d.totaldelayed);
      const len = largest.toString().length;
      if (len > max) max = len;
    });
    return max * 12 + 20;
  }, [driversSummary]);

  return (
    <div
      style={{
        position: "absolute",
        right: 12,
        top: 200,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 12,
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        maxHeight: 500,
        overflowY: "auto",
        background: "transparent",
      }}
    >
      {driversSummary.map((d) => {
        const isSelected = selectedDriverId === d.driverID;
        return (
          <div
            key={d.driverID}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
              background: isSelected ? "rgba(59, 130, 246, 0.5)" : "transparent",
              borderRadius: 6,
              padding: "4px 4px",
            }}
          >
            <Button
              label={d.driverName}
              tooltip={`Executive: ${d.driverName}`}
              tooltipOptions={{ position: "top" }}
              onClick={() => {
                handleDriverOrdersClick(d.driverID, "total");
              }}
              style={{
                color: "white",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "0.5rem",
                background: d.color,
                justifyContent: "center",
                width: maxNameWidth,
                textAlign: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />

            <Button
  tooltip="Assigned Orders"
  tooltipOptions={{ position: "top" }}
  onClick={() => handleDriverOrdersClick(d.driverID, "assigned")}
  style={{
    background: "#6b7280",
    color: "white",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: "0.5rem",
    width: maxCountWidth,
    textAlign: "center",
    justifyContent: "center",
  }}
>
  {d.assigned}
</Button>


            {makeBtn(d.driverID, d.delivered, "delivered", "Delivered")}
            {makeBtn(d.driverID, d.pending, "pending", historical ? "Delivery time out / pending outcomes" : "Yet to deliver / delivery time out")}
            {makeBtn(d.driverID, d.totaldelayed, "delayed", "Delayed orders")}
            {makeBtn(d.driverID, d.cancelled, "cancelled", "Cancelled")}
            {!!d.unknown && <Button label={`Unknown: ${d.unknown}`} severity="secondary" outlined size="small"
              onClick={() => handleDriverOrdersClick(d.driverID, "unknown")} />}
          </div>
        );
      })}
    </div>
  );
}


